import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useGetInstockFabricOptions } from '../components/use-get-instock-fabric-options';
import { forMatLabel } from '../lib/format-label';
import { getOptionsSelections } from '../lib/get-optionSelections';
import type { OptionsRoot, SwatchDataValue } from '../types/data';
import type { ProductRoot } from '../types/product';
import { useAddToCart } from './use-add-to-cart';
import { useGetMediaGalleryEntries } from './use-get-mediaGalleryEntries';

const compareStringsIgnoreCaseAndSpace = (str1: string, str2: string): boolean => {
  if (!str1 || !str2) {
    return false;
  }

  return str1.toLowerCase().replace(/\s/g, '') === str2.toLowerCase().replace(/\s/g, '');
};

export const useProductForm = ({
  options,
  product,
  isTryOn,
  instockSku,
  shouldSelectFirstColor,
  defaultColorLabel,
  searchParamsRecord,
  clearCheckCustomSizeRecord,
}: {
  options: OptionsRoot;
  product: ProductRoot;
  isTryOn: boolean;
  instockSku?: string;
  shouldSelectFirstColor?: boolean;
  defaultColorLabel?: string;
  searchParamsRecord?: Record<string, string | string[] | undefined>;
  clearCheckCustomSizeRecord?: () => void;
}) => {
  const shippingTypeIsDresses = product.shipping_type === 'Dresses';
  const isFloorLengthSizeDress =
    product.show_attribute?.find(item => item.code === 'aw_dresses_hemline')?.value === 'Floor Length';
  const [currrentSelectSizeLabel, setCurrrentSelectSizeLabel] = useState<string>('');
  const FormSchema: any = useMemo(() => {
    const schemaFields: Record<string, z.ZodTypeAny> = {};

    options.forEach(item => {
      if (item?.label?.toLowerCase().includes('color')) {
        schemaFields[`Color`] = z.object(
          {
            value_index: z.string().min(1),
            label: z.string(),
            price: z.number().optional(),
            option_id: z.string().optional(),
          },
          {
            message: `This is a required field!`,
          },
        );
      } else {
        schemaFields[`${forMatLabel(item?.label)}`] = z.object(
          {
            value_index: z.string().min(1),
            label: z.string(),
            price: z.number().optional(),
            option_id: z.string().optional(),
          },
          {
            message: `This is a required field!`,
          },
        );
      }
    });

    schemaFields['quantity'] = z.string().min(1, {
      message: `This is a required field!`,
    });

    if (currrentSelectSizeLabel === 'customized_size') {
      const customSizeFields = [
        {
          name: 'bust',
          label: 'Bust:',
          hint: '76-173 cm',
          min: 76,
          max: 173,
        },
        {
          name: 'waist',
          label: 'Waist:',
          hint: '56-178 cm',
          min: 56,
          max: 178,
        },
        {
          name: 'hip',
          label: 'Hip:',
          hint: '76-191 cm',
          min: 76,
          max: 191,
        },
        {
          name: shippingTypeIsDresses ? 'hollowtoHem' : 'hollowToFloor',
          label: shippingTypeIsDresses ? 'Hollow to Hem:' : 'Hollow to Floor:',
          hint: '76-178 cm',
          min: 76,
          max: 178,
        },
        {
          name: 'height',
          label: 'Height:',
          hint: '127-229 cm',
          min: 127,
          max: 229,
        },
        isFloorLengthSizeDress
          ? {
              name: 'extraLength',
              label: 'Extra Length:',
              hint: '0-15 cm',
              min: 0,
              max: 15,
            }
          : null,
      ].filter(x => !!x);

      customSizeFields.forEach(field => {
        schemaFields[field.name] = z
          .string({
            message: 'This is a required field!',
          })
          .refine(
            value => {
              const numValue = parseFloat(value);

              return !isNaN(numValue) && numValue >= field.min && numValue <= field.max;
            },
            {
              message: `The value is not within the specified range.`,
            },
          );
      });

      schemaFields['agreement'] = z
        .boolean({
          message: 'This is a required field!',
        })
        .refine(value => value === true, {
          message: 'This is a required field!',
        });

      schemaFields['length'] = z.string().optional();

      schemaFields['pocket'] = z.string().optional();

      schemaFields['backDetail'] = z.string().optional();
    }

    if (isTryOn) {
      schemaFields['isTryOn'] = z
        .boolean({
          message: 'This is a required field!',
        })
        .refine(value => value === true, {
          message: 'This is a required field!',
        });
    }

    if (shippingTypeIsDresses && currrentSelectSizeLabel === 'customized_size') {
      return z
        .object(schemaFields)
        .refine(data => data.height && data.hollowtoHem && parseFloat(data.height) >= parseFloat(data.hollowtoHem), {
          message: 'Ensure hollow to hem ≤ height. Please adjust.',
          path: ['height'],
        })
        .refine(data => data.height && data.hollowtoHem && parseFloat(data.height) >= parseFloat(data.hollowtoHem), {
          message: 'Ensure hollow to hem ≤ height. Please adjust.',
          path: ['hollowtoHem'],
        });
    }

    return z.object(schemaFields);
  }, [currrentSelectSizeLabel, isTryOn, options, isFloorLengthSizeDress, shippingTypeIsDresses]);

  const hasOnlyOneColor = options.filter(item => item?.label?.toLowerCase().includes('color'))?.length === 1; // 只有一个颜色
  type FormValues = z.infer<typeof FormSchema>;

  const urlFabric = searchParamsRecord?.['fabric'];
  const fabricOptions = options?.find(item => item?.label?.toLowerCase().includes('fabric'));

  const oldFabricOptionValues = fabricOptions?.values || [];

  const { values: fabricOptionValues } = useGetInstockFabricOptions({
    product,
    oldValues: oldFabricOptionValues,
  });

  const fabricOptionValue = fabricOptionValues?.find(item => item?.label === urlFabric);

  const defaultFabricValue =
    fabricOptionValue || fabricOptionValues?.find(item => item?.label?.includes('default')) || fabricOptionValues?.[0];

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: options.reduce(
      (acc, ampDataItem) => {
        if (ampDataItem?.label === 'Fabric') {
          const defaultValues = defaultFabricValue;

          acc[ampDataItem?.label] = defaultValues
            ? {
                value_index: defaultValues?.value_index.toString(),
                label: defaultValues?.label,
                price: defaultValues?.price,
                option_id: defaultValues?.option_id?.toString() || (ampDataItem as any)?.attribute_id,
              }
            : undefined;
        } else if (ampDataItem?.label?.toLowerCase().startsWith('color')) {
          let urlColor = searchParamsRecord?.['color'];
          urlColor = !urlColor || Array.isArray(urlColor) ? '' : urlColor;

          // amp 有 fabric 的情况下
          if (defaultFabricValue) {
            const defaultFabricLabel = defaultFabricValue.label.replace('-default', '');

            if (ampDataItem?.label?.includes(defaultFabricLabel)) {
              const defaultValues = ampDataItem?.values?.find(item =>
                compareStringsIgnoreCaseAndSpace(item?.label || '', defaultColorLabel || urlColor || ''),
              );

              if (defaultValues) {
                acc['Color'] = {
                  value_index: defaultValues?.value_index?.toString(),
                  label: defaultValues?.label,
                  price: defaultValues?.price,
                  option_id: defaultValues?.option_id?.toString(),
                };
              }
            }
          } else {
            const defaultValues = ampDataItem?.values?.find(item =>
              compareStringsIgnoreCaseAndSpace(item?.label || '', defaultColorLabel || urlColor || ''),
            );

            // 来自 url
            if (defaultValues) {
              acc['Color'] = {
                value_index: defaultValues?.value_index?.toString(),
                label: defaultValues?.label,
                price: defaultValues?.price,
                option_id: defaultValues?.option_id?.toString(),
              };
            } else if (shouldSelectFirstColor) {
              const firstColor = ampDataItem?.values?.[0];

              acc['Color'] = {
                value_index: firstColor?.value_index?.toString(),
                label: firstColor?.label,
                option_id: (ampDataItem as any)?.attribute_id,
                price: firstColor?.price,
              };
            }
          }
        } else if (ampDataItem?.label === 'Size') {
          let urlSize = searchParamsRecord?.['size'];
          urlSize = !urlSize || Array.isArray(urlSize) ? '' : urlSize;

          const defaultValues = ampDataItem?.values?.find(item =>
            compareStringsIgnoreCaseAndSpace(item?.label || '', urlSize || ''),
          ) as SwatchDataValue;

          if (!defaultValues) {
            acc[forMatLabel(ampDataItem?.label)] = undefined;
          } else {
            acc[forMatLabel(ampDataItem?.label)] = {
              value_index: defaultValues?.value_index?.toString(),
              label: defaultValues?.label,
              price: defaultValues?.price,
              option_id: defaultValues?.option_id?.toString(),
            };
          }
        } else {
          acc[forMatLabel(ampDataItem?.label)] = undefined;
        }

        return acc;
      },
      {
        quantity: '1',
      } as FormValues,
    ),
  });

  const { watch, setValue, formState, clearErrors } = form;

  const { errors, isValid } = formState;

  const allFormData = watch();

  const selectedFabric = watch('Fabric');
  const selectedSize = watch('Size');
  const selectedColor = watch('Color');
  const quantity = watch('quantity');

  // 由于自定义尺码的存在, size 切换的时候, formschema 也需要变化
  useEffect(() => {
    setCurrrentSelectSizeLabel(selectedSize?.label || '');
  }, [selectedSize]);

  // 监听面料变化, 如果面料变化, 颜色自动选中相同颜色
  useEffect(() => {
    if (selectedFabric && !hasOnlyOneColor) {
      const fabricName = options
        .find(d => d.label === 'Fabric')
        ?.values.find(v => v.value_index.toString() === selectedFabric?.value_index)
        ?.label.replace('-default', '');

      if (fabricName) {
        const colorField = `Color-${fabricName}`;
        const colorOptions = options.find(d => d.label.includes(colorField))?.values;

        if (colorOptions && colorOptions.length > 0) {
          // 找到相同颜色的选项
          const sameColorOption = colorOptions.find(option => option.label === watch('Color')?.label);

          if (sameColorOption) {
            setValue('Color', {
              value_index: sameColorOption.value_index.toString(),
              label: sameColorOption.label,
              price: sameColorOption.price,
              sku: sameColorOption.sku,
              option_id: sameColorOption.option_id.toString(),
            });
          } else {
            // 如果找不到相同颜色，设置为 undefined
            setValue('Color', undefined);
          }
        } else {
          // 如果没有颜色选项，设置为 undefined
          setValue('Color', undefined);
        }
      }
    }
  }, [selectedFabric, watch, setValue, hasOnlyOneColor, options]);

  const handleFabricChange = useCallback(
    (fabricLabel: string) => {
      const fabricOption = options?.find(item => item?.label?.toLowerCase().includes('fabric'));
      const newFabricValue = fabricOption?.values?.find(item => item?.label === fabricLabel);

      if (newFabricValue) {
        setValue('Fabric', {
          value_index: newFabricValue.value_index.toString(),
          label: newFabricValue.label,
          price: newFabricValue.price,
          option_id: newFabricValue.option_id?.toString(),
        });
      }
    },
    [options, setValue],
  );

  // 添加新的 useEffect 来监听 selectedSize 的变化
  // useEffect(() => {
  //     if (selectedSize?.label === "customized_size") {
  //         // 如果选择了自定义尺寸，将这些字段设置为必填
  //         clearErrors()
  //     } else {
  //         // 如果没有选择自定义尺寸，将这些字段设置为可选
  //         unregister("bust")
  //         unregister("waist")
  //         unregister("hip")
  //         unregister("hollowToFloor")
  //         unregister("height")
  //         unregister("extraLength")

  //         unregister("agreement")

  //         unregister("length")
  //         unregister("pockets")
  //         unregister("backDetail")
  //     }
  // }, [selectedSize, register, unregister, clearErrors])

  // map
  const optionSelections = useMemo(() => {
    const selections = getOptionsSelections(allFormData);

    return selections;
  }, [allFormData]);

  const { mediaGalleryEntries } = useGetMediaGalleryEntries({
    product,
    optionSelections,
  });

  const { handleAddToCart, isAdding, addToCartStatus, errorMessage, selectedOptionsArray } = useAddToCart({
    optionSelections,
    options,
    product,
    mediaGalleryEntries,
    form,
  });

  const onSubmit = (data: FormValues) => {
    handleAddToCart({
      allFormData: data,
      product,
      in_stock_sku: instockSku || '',
      afterSubmit: () => {
        // form.reset()
        form.clearErrors();
      },
      clearCheckCustomSizeRecord,
    });

    // 如果没有选择自定义尺寸，将这些字段设置为可选
    setValue('bust', '');
    setValue('waist', '');
    setValue('hip', '');
    if (shippingTypeIsDresses) {
      setValue('hollowtoHem', '');
    } else {
      setValue('hollowToFloor', '');
    }
    setValue('height', '');
    setValue('extraLength', '');
  };

  // useEffect(() => {
  //   const subscription = watch(() => {
  //     console.log('clear errors');
  //     clearErrors();
  //   });

  //   return () => subscription.unsubscribe();
  // }, [watch, clearErrors, formState.errors]);

  return {
    form,
    onSubmit,
    errors,
    allFormData,
    selectedColor,
    selectedSize,
    FormSchema,
    hasOnlyOneColor,
    selectedFabric,
    optionSelections,
    mediaGalleryEntries,
    isAdding,
    addToCartStatus,
    quantity,
    errorMessage,
    isValid,
    selectedOptionsArray,
    handleFabricChange,
  };
};
